import { defineStore } from 'pinia';
import { crmService } from '@/services/crmService';

export const useCrmStore = defineStore('crm', {
  state: () => ({
    leads: [],
    currentLead: null,
    loading: false,
    error: null,
    pagination: {
      total: 0,
      page: 1,
      limit: 20,
      totalPages: 0,
    },
    proposals: [],
    proposalPagination: {
      total: 0,
      page: 1,
      limit: 100,
      totalPages: 0,
    },
    leadSubmissions: [],
    leadSubmissionsLoading: false,
    leadSubmissionsPagination: {
      total: 0,
      page: 1,
      limit: 20,
      totalPages: 0,
    },
    leadAppointments: [],
    leadAppointmentsLoading: false,
    leadAppointmentsPagination: {
      total: 0,
      page: 1,
      limit: 20,
      totalPages: 0,
    },
  }),

  getters: {
    getLeadById: (state) => (id) => {
      return state.leads.find((lead) => lead.id === id);
    },
  },

  actions: {
    async fetchLeads(params = {}) {
      try {
        this.loading = true;
        const response = await crmService.getLeads(params);
        this.$patch((state) => {
          state.leads = response.leads || [];
          state.pagination = response.pagination;
          state.error = null;
        });
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async searchLeads(searchTerm) {
      try {
        const response = await crmService.getLeads({
          page: 1,
          limit: 100,
          search: searchTerm,
        });
        return response.leads;
      } catch (error) {
        console.error('Error searching leads:', error);
        throw error;
      }
    },

    async loadLead(id) {
      try {
        this.loading = true;
        const lead = await crmService.getLeadById(id);
        this.$patch((state) => {
          state.currentLead = lead;
          state.error = null;
        });
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async updateLead(id, leadData) {
      try {
        this.loading = true;
        const updatedLead = await crmService.updateLead(id, leadData);

        const index = this.leads.findIndex((l) => l.id === id);
        if (index !== -1) {
          this.leads[index] = updatedLead;
        }

        if (this.currentLead?.id === id) {
          this.currentLead = updatedLead;
        }

        this.error = null;
        return updatedLead;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async deleteLead(id) {
      try {
        this.loading = true;
        await crmService.deleteLead(id);

        this.$patch((state) => {
          state.leads = state.leads.filter((lead) => lead.id !== id);
          if (state.currentLead?.id === id) {
            state.currentLead = null;
          }
          state.error = null;
        });
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async archiveLead(id) {
      try {
        this.loading = true;
        const updatedLead = await crmService.archiveLead(id);

        this.$patch((state) => {
          // Remove from active leads list
          state.leads = state.leads.filter((lead) => lead.id !== id);
          if (state.currentLead?.id === id) {
            state.currentLead = updatedLead;
          }
          state.error = null;
        });
        return updatedLead;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async restoreLead(id) {
      try {
        this.loading = true;
        const updatedLead = await crmService.restoreLead(id);

        this.$patch((state) => {
          if (state.currentLead?.id === id) {
            state.currentLead = updatedLead;
          }
          state.error = null;
        });
        return updatedLead;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    clearCurrentLead() {
      this.currentLead = null;
    },

    async addLeadActivity(leadId, activity) {
      try {
        this.loading = true;
        const newActivity = await crmService.addLeadActivity(leadId, activity);
        this.$patch((state) => {
          state.currentLead.activities.push(newActivity);
          state.error = null;
        });
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async fetchProposals(params = {}, append = false) {
      try {
        this.loading = true;
        // Default status filter: only show active proposals (not superseded, withdrawn); includes sending
        const defaultParams = {
          status: 'draft,sending,sent,viewed,accepted,rejected',
          ...params,
        };
        const response = await crmService.getOrganizationProposals(defaultParams);
        this.$patch((state) => {
          if (append) {
            // Append new proposals to existing ones
            const existingIds = new Set(state.proposals.map(p => p.id));
            const newProposals = response.proposals.filter(p => !existingIds.has(p.id));
            state.proposals = [...state.proposals, ...newProposals];
          } else {
            // Replace proposals
            state.proposals = response.proposals;
          }
          state.proposalPagination = {
            total: response.pagination.total,
            currentPage: response.pagination.current_page,
            lastPage: response.pagination.last_page,
            perPage: response.pagination.per_page,
          };
          state.error = null;
        });
        return response.data;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async fetchLeadProposals(leadId, params = {}) {
      try {
        this.loading = true;
        const response = await crmService.getProposals(leadId, params);
        this.$patch((state) => {
          state.proposals = response.proposals;
          state.proposalPagination = {
            total: response.pagination.total,
            page: response.pagination.current_page || params.page || 1,
            limit: response.pagination.per_page || params.limit || 20,
            totalPages: response.pagination.last_page,
          };
          state.error = null;
        });
        return response;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async fetchLeadSubmissions(leadId, params = {}) {
      try {
        this.leadSubmissionsLoading = true;
        const response = await crmService.getLeadSubmissions(leadId, params);
        this.$patch((state) => {
          state.leadSubmissions = response.submissions;
          state.leadSubmissionsPagination = {
            total: response.pagination.total,
            page: response.pagination.page || params.page || 1,
            limit: response.pagination.limit || params.limit || 20,
            totalPages: response.pagination.totalPages,
          };
          state.error = null;
        });
        return response;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.leadSubmissionsLoading = false;
      }
    },

    clearLeadSubmissions() {
      this.$patch((state) => {
        state.leadSubmissions = [];
        state.leadSubmissionsPagination = {
          total: 0,
          page: 1,
          limit: 20,
          totalPages: 0,
        };
      });
    },

    async fetchLeadAppointments(leadId, params = {}) {
      try {
        this.leadAppointmentsLoading = true;
        const response = await crmService.getLeadAppointments(leadId, params);
        this.$patch((state) => {
          state.leadAppointments = response.appointments;
          state.leadAppointmentsPagination = {
            total: response.pagination.total,
            page: response.pagination.page || params.page || 1,
            limit: response.pagination.limit || params.limit || 20,
            totalPages: response.pagination.totalPages,
          };
          state.error = null;
        });
        return response;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.leadAppointmentsLoading = false;
      }
    },

    clearLeadAppointments() {
      this.$patch((state) => {
        state.leadAppointments = [];
        state.leadAppointmentsPagination = {
          total: 0,
          page: 1,
          limit: 20,
          totalPages: 0,
        };
      });
    },
  },
});
